import React, { useEffect, useState, useContext, useRef } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { NavigationContext } from '../../context/NavigationContext';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './Tischkonten.css';
import { validateRoundTables } from './roundTableValidation';
import ValidationDisplay from './ValidationDisplay';
import * as XLSX from '@e965/xlsx';

interface RoundTable {
    id: number;
    name: string;
    number: number;
    iban: string;
    bic: string;
    kontoInhaber: string;
    bankName: string;
    billingName: string;
    billingEmail: string;
    billingPlz: string;
    billingCity: string;
    billingStreet: string;
    billingStreetNumber: string;
}

interface FilterState {
    tableNumber: string;
    tableName: string;
}

const TischkontenComponent: React.FC = () => {
    const authContext = useContext(AuthContext);
    const navigationContext = useContext(NavigationContext);
    const [roundTables, setRoundTables] = useState<RoundTable[]>([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [showFilters, setShowFilters] = useState<boolean>(false);
    const [filters, setFilters] = useState<FilterState>({
        tableNumber: '',
        tableName: '',
    });
    const fileInputRef = useRef<HTMLInputElement>(null);

    if (!authContext) {
        throw new Error('TischkontenComponent must be used within an AuthProvider');
    }
    if (!navigationContext) {
        throw new Error('TischkontenComponent must be used within a NavigationProvider');
    }
    const { token } = authContext;
    const { navigateTo } = navigationContext;

    useEffect(() => {
        const fetchRoundTables = async () => {
            try {
                const response = await fetch('/api/roundtables', {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch round tables');
                }

                const data: RoundTable[] = await response.json();
                setRoundTables(data);
                setError(null);
            } catch (error) {
                console.error('Error fetching round tables:', error);
                setError('Fehler beim Laden der Tischkonten');
                toast.error('Fehler beim Laden der Tischkonten');
            } finally {
                setLoading(false);
            }
        };

        fetchRoundTables();
    }, [token]);

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file type
        const allowedTypes = [
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        ];
        if (!allowedTypes.includes(file.type) && !file.name.match(/\.(xls|xlsx)$/i)) {
            toast.error('Bitte wählen Sie eine Excel-Datei aus (.xls oder .xlsx)');
            return;
        }

        setUploading(true);

        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await fetch('/api/roundtables/importExcel', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
                body: formData,
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Upload fehlgeschlagen');
            }

            // Check for redirect
            if (data.redirect) {
                toast.success('Datei erfolgreich hochgeladen!');
                navigateTo(data.redirect);
            } else {
                toast.success('Datei erfolgreich hochgeladen!');
            }
        } catch (error) {
            console.error('Error uploading file:', error);
            toast.error(error instanceof Error ? error.message : 'Fehler beim Hochladen der Datei');
        } finally {
            setUploading(false);
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleFilterChange = (field: keyof FilterState, value: string) => {
        setFilters(prev => ({
            ...prev,
            [field]: value,
        }));
    };

    const clearFilters = () => {
        setFilters({
            tableNumber: '',
            tableName: '',
        });
        setSearchTerm('');
    };

    const handleExportToExcel = () => {
        try {
            // Prepare data for export - use filtered data
            const exportData = filteredRoundTables.map((table) => ({
                'Tischnummer': table.number,
                'Tischname': table.name,
                'Kontoinhaber': table.kontoInhaber || '',
                'IBAN': table.iban || '',
                'BIC': table.bic || '',
                'Bank': table.bankName || '',
                'Rechnungsname': table.billingName || '',
                'E-Mail': table.billingEmail || '',
                'Straße': table.billingStreet || '',
                'Hausnummer': table.billingStreetNumber || '',
                'PLZ': table.billingPlz || '',
                'Stadt': table.billingCity || '',
            }));

            // Create workbook and worksheet
            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(exportData);

            // Set column widths for better readability
            ws['!cols'] = [
                { wch: 12 }, // Tischnummer
                { wch: 20 }, // Tischname
                { wch: 25 }, // Kontoinhaber
                { wch: 30 }, // IBAN
                { wch: 15 }, // BIC
                { wch: 25 }, // Bank
                { wch: 25 }, // Rechnungsname
                { wch: 30 }, // E-Mail
                { wch: 25 }, // Straße
                { wch: 12 }, // Hausnummer
                { wch: 10 }, // PLZ
                { wch: 20 }, // Stadt
            ];

            // Add worksheet to workbook
            XLSX.utils.book_append_sheet(wb, ws, 'Tischkonten');

            // Generate filename with current date
            const date = new Date().toISOString().split('T')[0];
            const filename = `Tischkonten_${date}.xlsx`;

            // Save file
            XLSX.writeFile(wb, filename);

            toast.success('Excel-Datei wurde erfolgreich heruntergeladen');
        } catch (error) {
            console.error('Error exporting to Excel:', error);
            toast.error('Fehler beim Exportieren der Excel-Datei');
        }
    };

    // Filter logic
    // Helper function to get duplicate table numbers
    const getDuplicateNumbers = (): Set<number> => {
        const numberMap = new Map<number, number>();
        roundTables.forEach(table => {
            numberMap.set(table.number, (numberMap.get(table.number) || 0) + 1);
        });
        const duplicates = new Set<number>();
        numberMap.forEach((count, number) => {
            if (count > 1) {
                duplicates.add(number);
            }
        });
        return duplicates;
    };

    const duplicateNumbers = getDuplicateNumbers();

    const handleDeleteRoundTable = async (tableId: number, tableName: string) => {
        const confirmed = window.confirm(
            `Möchten Sie das Duplikat "${tableName}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`
        );

        if (!confirmed) return;

        try {
            const response = await fetch(`/api/roundtables/${tableId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error('Failed to delete round table');
            }

            // Remove from local state
            setRoundTables(prev => prev.filter(table => table.id !== tableId));
            toast.success('Tischkonto erfolgreich gelöscht');
        } catch (error) {
            console.error('Error deleting round table:', error);
            toast.error('Fehler beim Löschen des Tischkontos');
        }
    };

    const filteredRoundTables = roundTables.filter((table) => {
        // Apply search term (free text search across all fields)
        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            const matchesSearch = 
                table.name.toLowerCase().includes(searchLower) ||
                table.number.toString().includes(searchLower) ||
                (table.iban && table.iban.toLowerCase().includes(searchLower)) ||
                (table.bic && table.bic.toLowerCase().includes(searchLower)) ||
                (table.kontoInhaber && table.kontoInhaber.toLowerCase().includes(searchLower)) ||
                (table.bankName && table.bankName.toLowerCase().includes(searchLower)) ||
                (table.billingName && table.billingName.toLowerCase().includes(searchLower)) ||
                (table.billingEmail && table.billingEmail.toLowerCase().includes(searchLower)) ||
                (table.billingCity && table.billingCity.toLowerCase().includes(searchLower)) ||
                (table.billingStreet && table.billingStreet.toLowerCase().includes(searchLower)) ||
                (table.billingPlz && table.billingPlz.toLowerCase().includes(searchLower));
            
            if (!matchesSearch) return false;
        }

        // Apply explicit filters
        if (filters.tableNumber && !table.number.toString().includes(filters.tableNumber)) {
            return false;
        }

        if (filters.tableName && !table.name.toLowerCase().includes(filters.tableName.toLowerCase())) {
            return false;
        }

        return true;
    });

    if (loading) {
        return <div className="tischkonten-loading">Lade Tischkonten...</div>;
    }

    if (error) {
        return <div className="tischkonten-error">{error}</div>;
    }

    if (uploading) {
        return <div className="tischkonten-loading">Datei wird hochgeladen...</div>;
    }

    return (
        <div className="tischkonten-container">
            <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={handleFileChange}
            />
            <div className="tischkonten-header">
                <div className="title-section">
                    <h2>Tischkonten</h2>
                    <button
                        className="download-button"
                        onClick={handleExportToExcel}
                        title="Tischkonten als Excel exportieren"
                        disabled={filteredRoundTables.length === 0}
                    >
                        📥
                    </button>
                </div>
                <div className="upload-section">
                    <button className="upload-button" onClick={handleUploadClick} disabled={uploading}>
                        📤 Upload Tabler World Extract
                    </button>
                    <a
                        href="https://de.roundtable.world/de/admin/settings/finance/bank-accounts/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="info-icon"
                        title="Daten gibt es in der TablerWorld hier: https://de.roundtable.world/de/admin/settings/finance/bank-accounts/"
                    >
                        ?
                    </a>
                </div>
            </div>
            
            <div className="search-filter-section">
                <div className="search-bar">
                    <span className="search-icon">🔍</span>
                    <input
                        type="text"
                        className="search-input"
                        placeholder="Suche in allen Feldern..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                        <button className="clear-search" onClick={() => setSearchTerm('')}>
                            ✕
                        </button>
                    )}
                </div>
                
                <button 
                    className="filter-toggle-button"
                    onClick={() => setShowFilters(!showFilters)}
                >
                    🔽 Filter
                </button>
            </div>

            {showFilters && (
                <div className="filter-panel">
                    <div className="filter-row">
                        <label>
                            Tischnummer:
                            <input
                                type="text"
                                placeholder="z.B. 123"
                                value={filters.tableNumber}
                                onChange={(e) => handleFilterChange('tableNumber', e.target.value)}
                            />
                        </label>
                        <label>
                            Tischname:
                            <input
                                type="text"
                                placeholder="z.B. Berlin"
                                value={filters.tableName}
                                onChange={(e) => handleFilterChange('tableName', e.target.value)}
                            />
                        </label>
                        <button className="clear-filters-button" onClick={clearFilters}>
                            Filter zurücksetzen
                        </button>
                    </div>
                </div>
            )}

            {/* Data Quality Validation - shown between search bar and results */}
            {(() => {
                const validationResult = validateRoundTables(roundTables);
                return (
                    <ValidationDisplay 
                        checks={validationResult.checks} 
                        isValid={validationResult.isValid}
                        showEmailAction={true}
                    />
                );
            })()}

            <div className="tischkonten-list">
                {filteredRoundTables.length === 0 ? (
                    <p className="no-data">
                        {roundTables.length === 0 
                            ? 'Keine Tischkonten vorhanden' 
                            : 'Keine Tischkonten entsprechen den Filterkriterien'}
                    </p>
                ) : (
                    filteredRoundTables.map((table) => (
                        <div key={table.id} className="roundtable-card">
                            <div className="roundtable-header">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
                                    <h3>{table.name}</h3>
                                    <span className="roundtable-number">Tisch #{table.number}</span>
                                </div>
                                {duplicateNumbers.has(table.number) ? (
                                    <button
                                        className="delete-duplicate-btn"
                                        onClick={() => handleDeleteRoundTable(table.id, table.name)}
                                        title="Duplikat löschen"
                                    >
                                        🗑️ Duplikat löschen
                                    </button>
                                ) : (
                                    <button
                                        className="edit-roundtable-btn"
                                        onClick={() => navigateTo(`/tischkonten/details/${table.id}`)}
                                        title="Bearbeiten"
                                    >
                                        ✏️
                                    </button>
                                )}
                            </div>
                            <div className="roundtable-content">
                                <div className="info-section">
                                    <h4>Kontoinformationen</h4>
                                    <div className="info-row">
                                        <span className="info-label">Kontoinhaber:</span>
                                        <span className="info-value">{table.kontoInhaber || '-'}</span>
                                    </div>
                                    <div className="info-row">
                                        <span className="info-label">IBAN:</span>
                                        <span className="info-value">{table.iban || '-'}</span>
                                    </div>
                                    <div className="info-row">
                                        <span className="info-label">BIC:</span>
                                        <span className="info-value">{table.bic || '-'}</span>
                                    </div>
                                    <div className="info-row">
                                        <span className="info-label">Bank:</span>
                                        <span className="info-value">{table.bankName || '-'}</span>
                                    </div>
                                </div>
                                <div className="info-section">
                                    <h4>Rechnungsadresse</h4>
                                    <div className="info-row">
                                        <span className="info-label">Name:</span>
                                        <span className="info-value">{table.billingName || '-'}</span>
                                    </div>
                                    <div className="info-row">
                                        <span className="info-label">E-Mail:</span>
                                        <span className="info-value">{table.billingEmail || '-'}</span>
                                    </div>
                                    <div className="info-row">
                                        <span className="info-label">Adresse:</span>
                                        <span className="info-value">
                                            {table.billingStreet || table.billingStreetNumber
                                                ? `${table.billingStreet} ${table.billingStreetNumber}`
                                                : '-'}
                                        </span>
                                    </div>
                                    <div className="info-row">
                                        <span className="info-label"></span>
                                        <span className="info-value">
                                            {table.billingPlz && table.billingCity
                                                ? `${table.billingPlz} ${table.billingCity}`
                                                : '-'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default TischkontenComponent;
